import type { CompoundEntityRef } from '@backstage/catalog-model';
import type { FC } from 'react';

import { isUserEntity } from '@backstage/catalog-model';
import { Icon } from '@iconify/react';
import {
  getEntityCatalogLink,
  ImageWithFallback,
  useGetEntityByRef,
} from '@ipen-portal/plugin-common-react';
import {
  Divider,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { tss } from 'tss-react/mui';

import type { EntityKeyRoleData } from '../types/EntityKeyRoleData';

const useStyles = tss.create(({ theme }) => ({
  divider: {
    backgroundColor: theme.palette.divider,
    height: 2,
  },
  icon: {
    color: theme.palette.common.black,
    display: 'flex',
    height: 56,
    marginLeft: -8,
    marginRight: 6,
    marginTop: theme.spacing(2),
    width: 56,
  },
  muiBeforeIcon: {
    height: 40,
    width: 40,
  },
  subText: {
    ...theme.typography.body1,
    color: theme.palette.common.black,
    fontSize: theme.typography.h5.fontSize,
  },
}));

type Props = EntityKeyRoleData & {
  classNames?: {
    actionButton?: string;
    divider?: string;
    icon?: string;
    item?: string;
    subText?: string;
    text?: string;
  };
  clickable?: boolean;
  entityRef?: CompoundEntityRef | string;
  fallback?: string;
  onlyIcon?: boolean;
};

export const KeyRoleListItem: FC<Props> = ({
  classNames,
  clickable = true,
  entityRef,
  fallback,
  iconOverride,
  onlyIcon = false,
  roleOptions,
}) => {
  const { classes, cx } = useStyles();
  const { value: entity } = useGetEntityByRef(entityRef);

  let displayName: string | undefined;
  if (entity && isUserEntity(entity)) {
    displayName = entity.spec?.profile?.displayName;
  } else if (fallback) {
    displayName = fallback;
  } else {
    displayName = entity?.metadata?.title;
  }

  const usernameDisplay = (
    <Typography variant="h5">
      {displayName ?? roleOptions.noValueLabel}
    </Typography>
  );

  const hasLink = entity && clickable;

  return (
    <>
      <ListItem
        aria-label={roleOptions.label}
        className={classNames?.item}
        component={hasLink ? Link : 'div'}
        title={roleOptions.label}
        to={hasLink ? getEntityCatalogLink(entity) : undefined}
      >
        <ListItemAvatar>
          <Tooltip title={roleOptions.label}>
            {iconOverride
              ? (
                  <Icon className={classes.muiBeforeIcon} icon={iconOverride} />
                )
              : (
                  <ImageWithFallback
                    className={cx(classes.icon, classNames?.icon)}
                    fallbackImage={(
                      <Icon
                        icon={
                          roleOptions.kind === 'Group'
                            ? 'mdi:account-group'
                            : 'mdi:account'
                        }
                      />
                    )}
                    image={roleOptions.icon}
                    label={roleOptions.label}
                  />
                )}
          </Tooltip>
        </ListItemAvatar>
        {!onlyIcon && (
          <>
            <ListItemText
              primary={usernameDisplay}
              primaryTypographyProps={{
                'aria-label': 'User',
                'className': classNames?.text,
              }}
              secondary={roleOptions.label}
              secondaryTypographyProps={{
                'aria-label': 'Role',
                'className': cx(classes.subText, classNames?.subText),
              }}
            />
            {hasLink && (
              <ListItemSecondaryAction className={classNames?.actionButton}>
                <Typography color="textPrimary">
                  <Icon fontSize={24} icon="material-symbols:chevron-right" />
                </Typography>
              </ListItemSecondaryAction>
            )}
          </>
        )}
      </ListItem>
      <Divider
        className={cx(classes.divider, classNames?.divider)}
        variant="inset"
      />
    </>
  );
};
